<template>
  <div id="home">
    <div id="hero">
      <div class="container">
        <div class="columns is-vcentered">
          <div class="column is-8 is-offset-2 has-text-centered">
            <h1 class="title-wide is-0 text color-white mb-4">
              <slot name="heading">
                License the music you actually want.
              </slot>
            </h1>
            <p class="text extra-large color-white mb-5 px-6">
              <slot name="tagline">
                Unlimited licensing from <strong>$9.90/month</strong> - exceptional music at an unbeatable price. Simple.
              </slot>
            </p>
            <div class="button-row">
              <slot name="hero-button-1">
                <router-link
                  id="cta-search-hero"
                  :to="browseMusicRoute"
                  class="button is-secondary is-large is-rounded is-accent"
                >
                  Browse Songs
                </router-link>
              </slot>
              <slot name="hero-button-2">
                <router-link
                  class="button is-secondary is-large is-rounded ghost-inverse"
                  :to="authPagesEnabled ? { name: 'create-account' } : { name: 'pricing' }"
                >
                  {{ authPagesEnabled ? 'Create free account' : $store.getters['subscriptions/signupButtonLabel'] }}
                </router-link>
              </slot>
            </div>
          </div>
        </div>
      </div>
    </div>
    <section class="dark strip">
      <div class="container">
        <div class="columns is-vcentered">
          <div class="column is-6 is-offset-3">
            <img
              src="@/assets/homepage-hero-logos.png"
              alt="Logos of brands that license Sonata music"
            >
          </div>
        </div>
      </div>
    </section>
    <section class>
      <div class="container">
        <div class="columns is-vcentered is-8">
          <div class="column is-5 is-offset-1">
            <h2 class="title-wide is-1 text">
              <slot name="section-1-heading">
                Real music now made affordable
              </slot>
            </h2>
            <p
              class="text xl color-bluegrey1 mb-5"
            >
              <slot name="section-1-text">
                Don't settle for stock. Each of our 3 plans give you unlimited access and keep the process simple - all at an unbeatable price.
              </slot>
            </p>
            <slot name="section-1-button">
              <router-link
                class="button is-secondary is-large is-rounded is-accent"
                :to="browseMusicRoute"
              >
                Browse Songs
              </router-link>
            </slot>
          </div>

          <div class="column is-4 is-offset-1">
            <router-link
              :to="{ name: 'pricing' }"
              tag="div"
              class="columns is-8 is-mobile"
              :style="{ cursor: 'pointer'}"
            >
              <div class="column is-narrow">
                <img
                  src="@/assets/icons/circle-personal.svg"
                  alt=""
                >
              </div>
              <div class="column">
                <h4 class="title-lowercase is-5 text mb-1">
                  <slot name="section-1-plans-personal-heading">
                    Personal &mdash; {{ personalPrice }} per month
                  </slot>
                </h4>
                <p class="text medium color-bluegrey1">
                  <slot name="section-1-plans-personal-text">
                    Covers content or podcasts you own. Perfect for YouTubers, Streamers and Wedding Filmmakers.
                  </slot>
                </p>
              </div>
            </router-link>

            <router-link
              :to="{ name: 'pricing' }"
              tag="div"
              class="columns is-8 is-mobile"
              :style="{ cursor: 'pointer'}"
            >
              <div class="column is-narrow">
                <img
                  src="@/assets/icons/circle-commercial.svg"
                  alt=""
                >
              </div>
              <div class="column">
                <h4 class="title-lowercase is-5 text mb-1">
                  <slot name="section-1-plans-commercial-heading">
                    Commercial &mdash; {{ commercialPrice }} per month
                  </slot>
                </h4>
                <p class="text medium color-bluegrey1">
                  <slot name="section-1-plans-commercial-text">
                    You're a freelancer or business creating commercial productions. Great if you’re making social ads or content for a client.
                  </slot>
                </p>
              </div>
            </router-link>

            <router-link
              :to="{ name: 'pricing' }"
              tag="div"
              class="columns is-8 is-mobile"
              :style="{ cursor: 'pointer'}"
            >
              <div class="column is-narrow">
                <img
                  src="@/assets/icons/circle-extended.svg"
                  alt=""
                >
              </div>
              <div class="column">
                <h4 class="title-lowercase is-5 text mb-1">
                  <slot name="section-1-plans-extended-heading">
                    Extended &mdash; {{ extendedPrice }} per month
                  </slot>
                </h4>
                <p class="text medium color-bluegrey1">
                  <slot name="section-1-plans-extended-text">
                    For the big screen! The right plan for freelancers, businesses, broadcasters working on TV shows, TV ads and cinema.
                  </slot>
                </p>
              </div>
            </router-link>
          </div>
        </div>
      </div>
    </section>
    <section class="dark vinyl">
      <div class="container">
        <div class="columns is-vcentered">
          <div class="column is-5 is-offset-1">
            <img
              src="@/assets/homepage-artists.png"
              width="100%"
              alt="Collage of Sonata artists"
              loading="lazy"
            >
          </div>
          <div class="column is-4 is-offset-1">
            <h2 class="title-wide is-1 text color-white">
              <slot name="section-2-heading">
                Better music
              </slot>
            </h2>
            <p
              class="text xl color-white margin-bottom-lg"
            >
              <slot name="section-2-text">
                No artificial flavours here, our catalogue is meticulously curated &mdash; unlike others we only accept 10% of the artists who go through our A&R process. Exclusively featuring top artists, labels and composers. From Vlog to brand films, we've got you covered. Choose from thousands of royalty free tracks &mdash; updated weekly.
              </slot>
            </p>
          </div>
        </div>
      </div>
    </section>

    <section id="search">
      <div class="container">
        <div class="columns is-vcentered is-centered">
          <div class="column is-6 is-offset-3">
            <h2 class="title-wide is-1 text">
              <slot name="section-3-heading">
                Powerful search
              </slot>
            </h2>
            <img
              src="@/assets/homepage-waveform.svg"
              alt=""
              loading="lazy"
            >
            <p
              class="text xl color-bluegrey0 is-centered"
            >
              <slot name="section-3-text">
                Our search engine has advanced features (with the help of AI), making finding the perfect track easy, or find your inspiration from our playlists. Designed with direct input from professional creators.
              </slot>
            </p>
          </div>
          <div class="column is-3" />
        </div>
      </div>
    </section>

    <section class="dark strip">
      <div class="container">
        <div class="columns">
          <div class="column is-2" />
          <div class="column is-8">
            <div class="columns mt-3">
              <div class="column is-one-quarter">
                <div class="columns is-mobile">
                  <div class="column is-narrow">
                    <h2 class="title is-1 text color-white">
                      1
                    </h2>
                  </div>
                  <div class="column">
                    <p
                      class="text large color-white margin-bottom-lg"
                    >
                      Find the perfect track with curated playlists and search
                    </p>
                  </div>
                </div>
              </div>
              <div class="column is-one-quarter">
                <div class="columns is-mobile">
                  <div class="column is-narrow">
                    <h2 class="title is-1 text color-white">
                      2
                    </h2>
                  </div>
                  <div class="column">
                    <p
                      class="text large color-white margin-bottom-lg"
                    >
                      Pick from 3 simple plans based on your needs.
                    </p>
                  </div>
                </div>
              </div>
              <div class="column is-one-quarter">
                <div class="columns is-mobile">
                  <div class="column is-narrow">
                    <h2 class="title is-1 text color-white">
                      3
                    </h2>
                  </div>
                  <div class="column">
                    <p
                      class="text large color-white margin-bottom-lg"
                    >
                      Download as many tracks as you like for your project.
                    </p>
                  </div>
                </div>
              </div>
              <div class="column is-one-quarter">
                <div class="columns is-mobile">
                  <div class="column is-narrow">
                    <h2 class="title is-1 text color-white">
                      4
                    </h2>
                  </div>
                  <div class="column">
                    <p
                      class="text large color-white margin-bottom-lg"
                    >
                      Distribute your content legally and free.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="column is-2" />
        </div>
      </div>
    </section>

    <section>
      <div class="container">
        <h2 class="title-wide is-1 text is-centered mb-6">
          Trusted by the world's best brands and creators
        </h2>
        <div class="columns is-vcentered mb-6 is-8">
          <div class="column is-4 is-offset-1">
            <img
              class="logo"
              src="@/assets/homepage-brands-logos.png"
              alt="Logos of brands that license Sonata music"
              loading="lazy"
            >
          </div>
          <div class="column is-5 is-offset-1">
            <img
              class="logo"
              src="@/assets/homepage-nike.jpg"
              alt="Still from a Nike campaign"
              loading="lazy"
            >
          </div>
        </div>
        <div class="columns">
          <div class="column is-4">
            <div class="mb-5">
              <img
                class="avatar is-pulled-left mr-4"
                src="@/assets/avatar-chudders.jpg"
                alt="Mike Chudley"
                loading="lazy"
              >
              <p class="text label-large color-bluegrey1">
                Mike Chudley
              </p>
              <p class="text title is-5 color-bluegrey1 mt-1">
                YouTuber
              </p>
            </div>
            <p class="text medium color-bluegrey1">
              “It's like having Spotify on in the background - that's how good the music is! Super fast to find tracks you want to use - recommend.”
            </p>
          </div>
          <div class="column is-4">
            <div class="mb-5">
              <img
                class="avatar is-pulled-left mr-4"
                src="@/assets/avatar-hoskin.jpg"
                alt="Nick Hoskin"
                loading="lazy"
              >
              <p class="text label-large color-bluegrey1">
                Nick Hoskin
              </p>
              <p class="text title is-5 color-bluegrey1 mt-1">
                Creative director @ Something inc.
              </p>
            </div>
            <p class="text medium color-bluegrey1">
              “Honestly for me this catalogue is a game changer. The quality of the music sits head and shoulders above the rest. Typically we would have worked with music supervisors, now we can instantly license what we need.”
            </p>
          </div>
          <div class="column is-4">
            <div class="mb-5">
              <img
                class="avatar is-pulled-left mr-4"
                src="@/assets/avatar-ajanaku.jpg"
                alt="Maurice Ajanaku"
                loading="lazy"
              >
              <p class="text label-large color-bluegrey1">
                Maurice Ajanaku
              </p>
              <p class="text title is-5 color-bluegrey1 mt-1">
                Marketing Director @ citizenM Hotels
              </p>
            </div>
            <p class="text medium color-bluegrey1">
              “At this price point it's a no brainer for my team. We use our business plan almost weekly for our social media content and the quality jump from stock music is massive - thanks guys.”
            </p>
          </div>
        </div>
      </div>
    </section>

    <section
      id="try-sonata"
      class="dark"
    >
      <div class="content">
        <div class="container">
          <div class="columns is-vcentered">
            <div class="column is-6 is-offset-3 has-text-centered">
              <h2 class="title-wide is-1 text color-white">
                Try Sonata now
              </h2>
              <p
                class="text xl color-white mb-5 px-6"
              >
                Unlimited licensing from $16/month - exceptional music &amp; SFX at an unbeatable price. Simple.
              </p>
              <div class="button-row">
                <slot name="section-try-sonata-button">
                  <router-link
                    class="button is-secondary is-large is-rounded is-accent"
                    :to="browseMusicRoute"
                  >
                    Browse Songs
                  </router-link>
                </slot>
              </div>
            </div>
            <div class="column is-3" />
          </div>
        </div>
      </div>
    </section>

    <footer class="footer page-footer">
      <div class="content">
        <div class="container">
          <div class="columns">
            <div class="column is-3">
              <img
                class="logo"
                src="@/assets/sonata-logo.svg"
                alt="Sonata"
                width="112"
                height="30"
              >
              <p>
                <strong>Sonata Sync Ltd</strong> is a company registered in England and Wales (11739072).
              </p>
              <p>
                <a href="https://soundscapeagency.com/">
                  <img
                    src="@/assets/soundscape-dark.png"
                    alt="Soundscape Agency"
                    width="108"
                    height="19"
                    loading="lazy"
                  >
                </a>
                <br>
                Sonata is a partner company of <a href="https://soundscapeagency.com/">Soundscape Agency</a>.
              </p>
            </div>
            <div class="column is-2 is-offset-1">
              <h4 class="title is-4">
                Navigation
              </h4>
              <p>
                <router-link :to="{ name: 'home' }">
                  Home
                </router-link>
                <br>
                <router-link :to="{ name: 'track-search' }">
                  Tracks
                </router-link>
                <br>
                <router-link :to="{ name: 'playlist-list' }">
                  Playlists
                </router-link>
                <br>
                <router-link :to="{ name: 'pricing' }">
                  Pricing
                </router-link>
                <br>
                <router-link :to="{ name: 'faq' }">
                  FAQ
                </router-link>
                <br>
                <router-link :to="{ name: 'artist-submissions' }">
                  <strong>Become a Sonata artist</strong>
                </router-link>
              </p>
            </div>
            <div class="column is-2 is-offset-1">
              <h4 class="title is-4">
                Legal
              </h4>
              <p>
                <router-link :to="{ name: 'user-terms' }">
                  Terms
                </router-link>
                <br>
                <a
                  href="https://www.iubenda.com/privacy-policy/14330431"
                  title="Privacy Policy "
                >Privacy Policy</a>
                <br>
                <a
                  href="https://www.iubenda.com/privacy-policy/14330431/cookie-policy"
                  title="Cookie Policy"
                >Cookie Policy</a>
              </p>
            </div>
            <div class="column is-2 is-offset-1">
              <h4 class="title is-4">
                Contact
              </h4>
              <p>
                <a href="mailto:hello@sonata.media">
                  <span
                    class="icon-static mail"
                    style="margin: -4px 0 -4px 0;"
                  />
                  hello@sonata.media
                </a>
              </p>
              <p>
                <a href="https://www.instagram.com/sonata.media/">
                  <span
                    class="icon-static instagram"
                    style="margin: -4px 0 -4px 0;"
                  />
                  sonata.media
                </a>
              </p>
              <p>
                10 Dock Street
                <br>London
                <br>E1 8JP
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
    <activate-account-modal
      v-if="showActivateAccountModal"
      :show="showActivateAccountModal"
      :partial-id="$route.params.partialId"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import ActivateAccountModal from '@/views/ActivateAccountModal.vue'
import { browseMusicRoute } from '@/utils/catalogue.js'
import { AUTH_PAGES_SWITCH } from '@/utils/authPages.js'

export default {
  name: 'LandingPage',

  components: {
    ActivateAccountModal
  },

  computed: {
    ...mapGetters({ isFeatureEnabled: 'waffle/isFeatureEnabled' }),

    browseMusicRoute: function () {
      return browseMusicRoute()
    },

    authPagesEnabled: function () {
      return this.isFeatureEnabled(AUTH_PAGES_SWITCH)
    },

    isLoggedIn: function () {
      return this.$store.getters['auth/isLoggedIn']
    },

    showActivateAccountModal: function () {
      return this.$route.meta.showActivateAccountModal === true
    },

    version: function () {
      return this.$route.meta.version || ''
    },

    personalPrice: function () {
      return '$16'
    },

    commercialPrice: function () {
      return '$30'
    },

    extendedPrice: function () {
      return '$80'
    }
  },

  metaInfo () {
    let url = window.location.origin
    const path = this.$route.path
    if (path !== '/') {
      url += path
    }
    const title = 'License Music By Artists For Video, Film, Adverts & Games'
    const description = 'Artists. Not Stock | Discover and license real music from emerging artists, bands and composers for your creative content. TV, Film, Adverts, Games. Carefully curated, simple pricing and ready to license, hassle-free.'

    return {
      title,
      meta: [
        { name: 'description', content: description },
        { property: 'og:url', content: url },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:image', content: '/img/sonata-meta.png' },
        { name: 'twitter:card', content: 'summary' }
      ]
    }
  }
}
</script>

<style scoped lang="scss">
@import "../assets/scss/_variables.scss";
#home #hero #cta-hero {
  margin-top: 12px;
  margin-bottom: 32px;
}
@media only screen and (max-width: $tablet) {
  .title-lowercase.is-0 {
    font-size: $size-1;
  }
  .title-lowercase.is-1 {
    font-size: $size-2;
  }
  #home #hero h1.title-lowercase {
    margin-bottom: 24px;
  }
  #home #hero h2.title-lowercase {
    font-size: $size-3;
    margin-top: 0;
    margin-bottom: 24px;
  }
  #try-sonata {
    text-align: center;
  }
}
footer.page-footer {
  background: $white;
  .logo {
    margin-bottom: 12px;
  }
}
</style>
